import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase.js';
import { compressImage } from '../lib/photos.js';
import CameraCapture from '../components/CameraCapture.jsx';

const API_URL = '/.netlify/functions/public-reclam';

async function api(action, params) {
  const res = await fetch(API_URL, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, params }),
  });
  if (res.status === 429) throw new Error('Trop de requêtes. Attendez une minute.');
  const json = await res.json();
  if (json.error) throw new Error(json.error);
  return json.result;
}

// PROBLEMS : libelles dynamiques singulier/pluriel selon le nombre de produits coches.
// La fonction getProblems(nbProduits) retourne la liste avec le bon libelle.
function getProblems(nbProduits) {
  const plural = nbProduits >= 2;
  return [
    { value: 'produit_manquant', label: plural ? 'Produits non reçus' : 'Produit non reçu', icon: 'M' },
    { value: 'reliquat', label: plural ? 'Reliquats (non livrés ni facturés)' : 'Reliquat (non livré ni facturé)', icon: 'R' },
    { value: 'erreur_produit', label: plural ? 'Mauvais produits reçus' : 'Mauvais produit reçu', icon: 'E' },
    { value: 'quantite_incorrecte', label: 'Quantité incorrecte', icon: 'Q' },
    { value: 'produit_casse', label: plural ? 'Produits endommagés' : 'Produit endommagé', icon: 'D' },
    { value: 'caisse_manquante', label: 'Caisse ou carton manquant', icon: 'C' },
    { value: 'mauvaise_caisse', label: 'Mauvaise caisse ou carton reçu', icon: 'R' },
    { value: 'commande_non_recue', label: 'Commande non reçue', icon: 'N' },
    { value: 'mauvaise_commande_recue', label: "J'ai reçu une commande qui n'est pas la mienne", icon: 'X' },
    // Trois motifs ajoutés d'après le réel : sur 4 mois, 71 réclamations
    // (29 %) ont été déposées en « Autre » alors qu'elles disaient toutes
    // l'une de ces trois choses — commandé par erreur, péremption trop
    // courte, écart entre la facture et le reçu.
    { value: 'retour_erreur_pharmacie', label: plural ? 'Produits commandés par erreur' : 'Produit commandé par erreur', icon: '↩' },
    { value: 'dlu_courte', label: 'Date de péremption trop courte', icon: 'P' },
    { value: 'ecart_facture', label: 'Écart entre la facture et ce que j\u2019ai reçu', icon: 'F' },
    { value: 'autre', label: 'Autre', icon: '?' },
  ];
}
// Liste fallback (ancien code qui reference PROBLEMS sans nb produits)
const PROBLEMS = getProblems(1);

// Options de souhait adaptées à chaque problème. Liste vide = pas de question.
// (mauvaise_commande_recue : reprise évidente ; autre : texte libre)
function getSouhaitOptions(problem) {
  switch (problem) {
    case 'produit_manquant':
    case 'caisse_manquante':
    case 'quantite_incorrecte':
    case 'produit_casse':
      return [
        { value: 'renvoi', label: '🔄 Renvoi du produit' },
        { value: 'avoir', label: '💶 Un avoir' },
      ];
    case 'commande_non_recue':
      return [
        { value: 'renvoi', label: '🚚 Nouvelle livraison de la commande' },
        { value: 'avoir', label: '💶 Un avoir' },
      ];
    case 'erreur_produit':
      return [
        { value: 'garde', label: '📦 Je garde ce produit' },
        { value: 'reprise', label: '↩️ Reprise du produit' },
      ];
    case 'mauvaise_caisse':
      return [
        { value: 'reprise', label: '↩️ Reprise par le chauffeur' },
        { value: 'garde', label: '📦 Je garde les produits (à me facturer)' },
      ];
    case 'retour_erreur_pharmacie':
      return [
        { value: 'avoir', label: '↩️ Reprise du produit et avoir' },
        { value: 'reprise', label: '↩️ Reprise du produit seulement' },
      ];
    case 'dlu_courte':
      return [
        { value: 'avoir', label: '↩️ Reprise du produit et avoir' },
        { value: 'remise', label: '💶 Une remise, je garde le produit' },
        { value: 'garde', label: '📦 Je garde le produit tel quel' },
      ];
    case 'ecart_facture':
      return [
        { value: 'avoir', label: '💶 Un avoir sur la différence' },
        { value: 'renvoi', label: '🔄 Recevoir ce qui manque' },
      ];
    case 'reliquat':
      return [
        { value: 'recevoir_reliquat', label: '⏳ Le recevoir dès disponibilité' },
        { value: 'annule_reliquat', label: '❌ Annuler le reliquat' },
      ];
    default:
      return [];
  }
}

const S = {
  page: { fontFamily: "'DM Sans', 'Segoe UI', system-ui, sans-serif", maxWidth: 480, margin: '0 auto', padding: '16px', minHeight: '100dvh', background: '#F8F9FB' },
  header: { textAlign: 'center', marginBottom: 20, paddingTop: 12 },
  logo: { fontSize: 18, fontWeight: 700, color: '#1A5276', letterSpacing: -0.5 },
  sub: { fontSize: 12, color: '#6B7280', marginTop: 4 },
  card: { background: 'white', borderRadius: 12, border: '1px solid #E5E7EB', padding: '14px', marginBottom: 8 },
  lbl: { fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 },
  ttl: { fontSize: 18, fontWeight: 700, color: '#1A1D23', marginBottom: 12 },
  input: { width: '100%', padding: '12px 14px', borderRadius: 10, border: '1.5px solid #D6E4F0', fontSize: 15, fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none' },
  pri: { width: '100%', padding: 14, borderRadius: 10, border: 'none', background: '#1A5276', color: 'white', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', marginTop: 8 },
  sec: { width: '100%', padding: 12, borderRadius: 10, border: '1.5px solid #E5E7EB', background: 'white', color: '#6B7280', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', marginTop: 6 },
  dis: { width: '100%', padding: 14, borderRadius: 10, border: 'none', background: '#E5E7EB', color: '#9CA3AF', fontSize: 14, fontWeight: 600, fontFamily: 'inherit', marginTop: 8, cursor: 'default' },
  pill: (active) => ({ padding: '8px 14px', borderRadius: 8, border: '1.5px solid ' + (active ? '#1A5276' : '#E5E7EB'), background: active ? '#EEF6FF' : 'white', color: active ? '#1A5276' : '#555', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 10, width: '100%' }),
};

function ProgressBar({ step, total }) {
  return (
    <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} style={{ flex: 1, height: 4, borderRadius: 2, background: i < step ? '#1A5276' : '#E5E7EB', transition: 'background 0.2s' }} />
      ))}
    </div>
  );
}

export default function ReclamationForm() {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState(null); // email masqué si confirmation envoyée
  const [error, setError] = useState(null);

  // Step 1: Pharmacy
  const [phQuery, setPhQuery] = useState('');
  const [phResults, setPhResults] = useState([]);
  const [phLoading, setPhLoading] = useState(false);
  const [pharmacy, setPharmacy] = useState(null);
  const phTimer = useRef(null);

  // Step 2: Orders - 2 modes (par produit+date OU par facture)
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  // Mode produit+date
  const [productQuery, setProductQuery] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [productResultsLoading, setProductResultsLoading] = useState(false);
  const [selectedProductSearch, setSelectedProductSearch] = useState(null); // { name, cip }
  const productTimerForm = useRef(null);
  // Mode facture
  const [invoiceQuery, setInvoiceQuery] = useState('');
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const invoiceTimer = useRef(null);
  // Source de recherche (pour traca vers le SAV)
  const [searchSource, setSearchSource] = useState(null); // 'produit DOLI' OU 'facture INV/2026/24226'

  // Step 3: Products
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  // Mode 'toute la commande' vs 'certains produits' (Step 3)
  const [wholeOrder, setWholeOrder] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]); // { id, name, qty, qtyReclamee, source }
  const [manualProduct, setManualProduct] = useState('');

  // Step 4: Problem type
  const [problem, setProblem] = useState(null);

  // Step 5: Details + photos
  const [comment, setComment] = useState('');
  // Souhait de la pharmacie (renvoi / avoir / indifferent) quand pertinent
  const [souhait, setSouhait] = useState(null);
  const [photos, setPhotos] = useState([]); // { preview, storagePath }
  const [camOpen, setCamOpen] = useState(false);

  // ═══ Step 1: Search pharmacy ═══
  useEffect(() => {
    if (phQuery.length < 3) { setPhResults([]); return; }
    clearTimeout(phTimer.current);
    phTimer.current = setTimeout(async () => {
      setPhLoading(true);
      try {
        const res = await api('verifyPharmacy', { query: phQuery });
        setPhResults(res.found ? res.results : []);
      } catch { setPhResults([]); }
      setPhLoading(false);
    }, 400);
  }, [phQuery]);

  function selectPharmacy(ph) {
    setPharmacy(ph);
    setPhQuery(ph.name);
    setPhResults([]);
    // Reset etat etape 2
    setOrders([]);
    setProductQuery(''); setProductResults([]); setSelectedProductSearch(null);
    setInvoiceQuery(''); setSearchSource(null);
    setStep(2);
  }

  // ═══ Step 2 : Autocomplete produit (90 derniers jours pour cette pharmacie) ═══
  useEffect(() => {
    if (!pharmacy?.id) return;
    if (selectedProductSearch) return; // produit choisi : ne plus chercher
    const q = productQuery.trim();
    clearTimeout(productTimerForm.current);
    if (q.length < 2) { setProductResults([]); return; }
    productTimerForm.current = setTimeout(async () => {
      setProductResultsLoading(true);
      try {
        const res = await api('searchProducts', { partnerId: pharmacy.id, query: q });
        setProductResults(res.products || []);
      } catch { setProductResults([]); }
      setProductResultsLoading(false);
    }, 400);
  }, [productQuery, pharmacy, selectedProductSearch]);

  // Lancer la recherche par produit (auto-declenchee quand selectedProductSearch change)
  async function searchByProduct() {
    if (!pharmacy?.id) return;
    const q = selectedProductSearch?.cip || selectedProductSearch?.name || productQuery.trim();
    if (!q) return;
    setOrdersLoading(true);
    setOrders([]);
    try {
      const res = await api('findOrders', {
        partnerId: pharmacy.id,
        productSearch: q,
      });
      setOrders(res.orders || []);
      // Tag de source pour traca SAV
      setSearchSource('produit "' + (selectedProductSearch?.name || productQuery.trim()) + '"');
    } catch (e) {
      setError(e.message);
    }
    setOrdersLoading(false);
  }

  // Lancer la recherche par facture (auto-declenchee apres 800ms de pause de frappe)
  async function searchByInvoice() {
    if (!pharmacy?.id || !invoiceQuery.trim()) return;
    setInvoiceLoading(true);
    setOrdersLoading(true);
    setOrders([]);
    try {
      const res = await api('findOrders', {
        partnerId: pharmacy.id,
        invoiceRef: invoiceQuery.trim(),
      });
      setOrders(res.orders || []);
      // Pas d alert ici (mode auto) : on laisse juste le message "Aucune commande" s afficher
      setSearchSource('facture ' + (res.invoice_name || invoiceQuery.trim()));
    } catch (e) {
      setError(e.message);
    }
    setInvoiceLoading(false);
    setOrdersLoading(false);
  }

  // ═══ Auto-search par produit : declenche des qu un produit est selectionne dans l autocomplete ═══
  useEffect(() => {
    if (selectedProductSearch && pharmacy?.id) {
      searchByProduct();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProductSearch?.product_id, selectedProductSearch?.cip, selectedProductSearch?.name]);

  // ═══ Auto-search par facture : declenche apres 800ms de pause de frappe ═══
  useEffect(() => {
    clearTimeout(invoiceTimer.current);
    if (!invoiceQuery.trim() || !pharmacy?.id) return;
    invoiceTimer.current = setTimeout(() => {
      searchByInvoice();
    }, 800);
    return () => clearTimeout(invoiceTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceQuery, pharmacy?.id]);

  // ═══ Step 2 (legacy fonctions retirees - remplacees par searchByProduct/searchByInvoice) ═══

  function selectOrder(order) {
    setSelectedOrder(order);
    loadProducts(order.session_id);
    setStep(3);
  }

  // ═══ Step 3: Load products ═══
  async function loadProducts(sessionId) {
    setProductsLoading(true);
    setWholeOrder(false);
    try {
      const res = await api('getSessionProducts', { sessionId });
      const allProducts = res.products || [];
      // Si la pharmacie a recherche par produit, on essaie de le retrouver dans
      // la liste (match sur CIP ou contient le nom) et on le met en premier + pre-coche.
      const searchQ = (selectedProductSearch?.cip || selectedProductSearch?.name || productQuery || '').toLowerCase().trim();
      if (searchQ.length >= 2) {
        // CIP match prioritaire
        const cipMatch = selectedProductSearch?.cip;
        const matched = [];
        const others = [];
        allProducts.forEach(p => {
          const name = (p.name || '').toLowerCase();
          const isMatch = (cipMatch && name.includes(cipMatch.toLowerCase()))
            || (name.includes(searchQ));
          if (isMatch && matched.length === 0) matched.push(p); // un seul match en premier
          else others.push(p);
        });
        setProducts([...matched, ...others]);
        // Pre-cocher le produit matched
        if (matched.length > 0) {
          const p = matched[0];
          setSelectedProducts([{ id: p.id, name: p.name, qty: p.qty, qtyReclamee: 1, source: 'auto' }]);
        } else {
          setSelectedProducts([]);
        }
      } else {
        setProducts(allProducts);
        setSelectedProducts([]);
      }
    } catch { setProducts([]); }
    setProductsLoading(false);
  }

  // Bascule entre 'toute la commande' et 'certains produits'
  function setMode(whole) {
    setWholeOrder(whole);
    if (whole) {
      // Quand 'Toute la commande' : on selectionne TOUS les produits de la liste
      setSelectedProducts(products.map(p => ({
        id: p.id, name: p.name, qty: p.qty, qtyReclamee: p.qty || 1, source: 'whole_order',
      })));
    } else {
      // Quand 'Certains produits' : on revient a la selection initiale (le produit recherche pre-coche)
      const searchQ = (selectedProductSearch?.cip || selectedProductSearch?.name || productQuery || '').toLowerCase().trim();
      if (searchQ.length >= 2) {
        const cipMatch = selectedProductSearch?.cip;
        const matched = products.find(p => {
          const name = (p.name || '').toLowerCase();
          return (cipMatch && name.includes(cipMatch.toLowerCase())) || name.includes(searchQ);
        });
        if (matched) {
          setSelectedProducts([{ id: matched.id, name: matched.name, qty: matched.qty, qtyReclamee: 1, source: 'auto' }]);
          return;
        }
      }
      setSelectedProducts([]);
    }
  }

  function toggleProduct(p) {
    setSelectedProducts(prev => {
      const exists = prev.find(x => x.id === p.id);
      if (exists) return prev.filter(x => x.id !== p.id);
      return [...prev, { id: p.id, name: p.name, qty: p.qty, qtyReclamee: 1, source: 'auto' }];
    });
  }

  function updateQtyReclamee(id, val) {
    setSelectedProducts(prev => prev.map(p => p.id === id ? { ...p, qtyReclamee: Math.max(1, parseInt(val) || 1) } : p));
  }

  function addManualProduct() {
    if (!manualProduct.trim()) return;
    const id = 'manual-' + Date.now();
    setSelectedProducts(prev => [...prev, { id, name: manualProduct.trim(), qty: 0, qtyReclamee: 1, source: 'manuel' }]);
    setManualProduct('');
  }

  // ═══ Step 5: Photos ═══
  async function onCapture(file) {
    const name = 'reclamations-public/' + Date.now() + '_' + Math.random().toString(36).slice(2, 8) + '.jpg';
    const toSend = await compressImage(file);
    const { error: upErr } = await supabase.storage.from('elixir-prep-photos').upload(name, toSend, { contentType: toSend.type || 'image/jpeg' });
    if (upErr) throw upErr;
    const reader = new FileReader();
    reader.onload = () => setPhotos(prev => [...prev, { preview: reader.result, storagePath: name }]);
    reader.readAsDataURL(file);
  }

  // ═══ Submit ═══
  async function handleSubmit() {
    setSubmitting(true);
    setError(null);
    try {
      // Enrichir le searchSource avec l info wholeOrder pour traca cote SAV
      const enrichedSource = wholeOrder
        ? (searchSource ? searchSource + ' · TOUTE LA COMMANDE' : 'TOUTE LA COMMANDE')
        : searchSource;
      const result = await api('submitReclamation', {
        partnerId: pharmacy?.id || null,
        partnerName: pharmacy?.name || phQuery,
        sessionId: selectedOrder?.session_id || null,
        orderRefs: selectedOrder?.refs || [],
        products: selectedProducts.map(p => ({
          name: p.name,
          qty: p.qtyReclamee,
          problem: problem,
          action: '',
        })),
        comment,
        photoPaths: photos.map(p => p.storagePath).filter(Boolean),
        searchSource: enrichedSource,
        souhait: getSouhaitOptions(problem).length > 0 ? souhait : null,
      });
      if (result?.emailSent && result?.emailTo) setConfirmEmail(result.emailTo);
      setSubmitted(true);
    } catch (e) {
      setError(e.message);
    }
    setSubmitting(false);
  }

  function fmtDate(d) {
    return new Date(d).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
  }

  // ═══ SUBMITTED ═══
  if (submitted) {
    return (
      <div style={S.page}>
        <div style={{ textAlign: 'center', paddingTop: 60 }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#D5F5E3', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: 28 }}>✓</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#1A1D23', marginBottom: 8 }}>Réclamation envoyée</div>
          <div style={{ fontSize: 14, color: '#6B7280', marginBottom: confirmEmail ? 8 : 24 }}>Nous revenons vers vous dans l'heure.</div>
          {confirmEmail && (
            <div style={{ fontSize: 12, color: '#1E6B3C', marginBottom: 24 }}>
              📧 Un email de confirmation a été envoyé à <strong>{confirmEmail}</strong>
            </div>
          )}
          <button onClick={() => window.location.reload()} style={S.sec}>Nouvelle réclamation</button>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div style={S.logo}>ELIXIR PHARMA</div>
        <div style={S.sub}>Formulaire de réclamation</div>
      </div>

      <ProgressBar step={step} total={5} />

      {/* ═══ STEP 1: Pharmacie ═══ */}
      {step === 1 && (
        <div>
          <div style={S.lbl}>Étape 1/5</div>
          <div style={S.ttl}>Votre pharmacie</div>
          <input
            style={S.input} type="text" value={phQuery}
            onChange={e => { setPhQuery(e.target.value); setPharmacy(null); }}
            placeholder="Tapez le nom de votre pharmacie..."
            autoFocus
          />
          {phLoading && <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 8, textAlign: 'center' }}>Recherche...</div>}
          {phResults.length > 0 && (
            <div style={{ marginTop: 8 }}>
              {phResults.map(ph => (
                <button key={ph.id} onClick={() => selectPharmacy(ph)} style={{ ...S.card, cursor: 'pointer', display: 'block', width: '100%', textAlign: 'left', border: '1.5px solid #E5E7EB' }}>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{ph.name}</div>
                  {ph.city && <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{ph.city}</div>}
                </button>
              ))}
            </div>
          )}
          {phQuery.length >= 3 && !phLoading && phResults.length === 0 && (
            <div style={{ textAlign: 'center', marginTop: 16, color: '#9CA3AF', fontSize: 13 }}>
              Pharmacie non trouvée
              <button onClick={() => { setPharmacy({ id: null, name: phQuery }); setStep(4); }} style={{ ...S.sec, marginTop: 8 }}>
                Continuer avec "{phQuery}"
              </button>
            </div>
          )}
        </div>
      )}

      {/* ═══ STEP 2: Identification de la commande (produit+date OU facture) ═══ */}
      {step === 2 && (
        <div>
          <div style={S.lbl}>Étape 2/5</div>
          <div style={S.ttl}>Comment identifier votre commande ?</div>
          <div style={{ fontSize: 13, color: '#555', marginBottom: 16 }}>
            {pharmacy?.name}
          </div>

          {/* ━━━━━━ ZONE A : Par produit ━━━━━━ */}
          <div style={{ padding: 12, borderRadius: 12, background: 'white', border: '1.5px solid #E5E7EB', marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#1A5276', marginBottom: 10 }}>
              🔍 PAR PRODUIT (recommandé)
            </div>
            {/* Autocomplete produit */}
            <input style={{ ...S.input, marginBottom: 0 }} type="text"
              value={productQuery}
              onChange={e => { setProductQuery(e.target.value); setSelectedProductSearch(null); }}
              placeholder="Nom ou CIP du produit (ex: DOLIPRANE)" />
            {productResultsLoading && (
              <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 6, textAlign: 'center' }}>Recherche…</div>
            )}
            {productResults.length > 0 && !selectedProductSearch && (
              <div style={{ marginTop: 6, maxHeight: 200, overflow: 'auto', border: '1px solid #E5E7EB', borderRadius: 8 }}>
                {productResults.map(p => (
                  <button key={p.product_id} onClick={() => { setSelectedProductSearch(p); setProductQuery(p.name); setProductResults([]); }}
                    style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px',
                      background: 'white', border: 'none', borderBottom: '1px solid #F3F4F6', cursor: 'pointer', fontFamily: 'inherit' }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{p.name}</div>
                    {p.cip && <div style={{ fontSize: 10, color: '#888', fontFamily: 'monospace' }}>{p.cip}</div>}
                  </button>
                ))}
              </div>
            )}
            {selectedProductSearch && (
              <div style={{ fontSize: 11, color: '#16A34A', marginTop: 6, fontWeight: 600 }}>
                ✓ Sélectionné · <button onClick={() => { setSelectedProductSearch(null); setProductQuery(''); setOrders([]); setSearchSource(null); }} style={{ background: 'none', border: 'none', color: '#1A5276', cursor: 'pointer', textDecoration: 'underline', fontSize: 11, fontFamily: 'inherit', padding: 0 }}>changer</button>
              </div>
            )}
          </div>

          {/* ━━━━━━ Separateur OU ━━━━━━ */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '12px 0', color: '#9CA3AF', fontSize: 11, fontWeight: 700 }}>
            <div style={{ flex: 1, height: 1, background: '#E5E7EB' }} />
            <span>OU</span>
            <div style={{ flex: 1, height: 1, background: '#E5E7EB' }} />
          </div>

          {/* ━━━━━━ ZONE B : Par n° facture ━━━━━━ */}
          <div style={{ padding: 12, borderRadius: 12, background: 'white', border: '1.5px solid #E5E7EB', marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#1A5276', marginBottom: 10 }}>
              📄 PAR N° DE FACTURE
            </div>
            <input style={{ ...S.input, marginBottom: 0 }} type="text"
              value={invoiceQuery}
              onChange={e => setInvoiceQuery(e.target.value)}
              placeholder="ex : INV/2026/24226 ou 24226" />
            {invoiceLoading && (
              <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 6, textAlign: 'center' }}>Recherche…</div>
            )}
          </div>

          {ordersLoading && <div style={{ textAlign: 'center', color: '#9CA3AF', fontSize: 12, marginTop: 12 }}>Recherche en cours…</div>}

          {/* Liste commandes trouvees */}
          {orders.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 600, marginBottom: 6 }}>
                {orders.length} commande{orders.length > 1 ? 's' : ''} trouvée{orders.length > 1 ? 's' : ''}
              </div>
              {orders.map((o, i) => (
                <button key={i} onClick={() => selectOrder(o)} style={{ ...S.card, cursor: 'pointer', display: 'block', width: '100%', textAlign: 'left', border: '1.5px solid #E5E7EB', marginBottom: 6 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, fontFamily: 'monospace' }}>{o.refs.filter(r => !r.startsWith('[R')).join(', ') || '—'}</div>
                      <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{fmtDate(o.date)}</div>
                    </div>
                    <div style={{ textAlign: 'right', fontSize: 11, color: '#888' }}>
                      {o.nb_amb > 0 && <div>{o.nb_amb} amb</div>}
                      {o.nb_froid > 0 && <div>{o.nb_froid} froid</div>}
                      {o.nb_cartons > 0 && <div>{o.nb_cartons} cartons</div>}
                    </div>
                  </div>
                  {o.preparateur && <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 4 }}>Préparé par {o.preparateur}</div>}
                </button>
              ))}
            </div>
          )}

          {!ordersLoading && orders.length === 0 && (searchSource) && (
            <div style={{ marginTop: 16, padding: 14, borderRadius: 10, background: '#FEF3CD', border: '1.5px solid #F0C36D' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#92710C', marginBottom: 6, textAlign: 'center' }}>
                ⚠️ Aucune commande trouvée
              </div>
              <div style={{ fontSize: 12, color: '#7C5A0C', textAlign: 'center', lineHeight: 1.4 }}>
                Vous pouvez continuer votre réclamation sans identifier de commande précise — nous traiterons votre demande manuellement.
              </div>
            </div>
          )}

          <button onClick={() => { setSelectedOrder(null); setSearchSource(searchSource || 'saisie manuelle (sans commande)'); setStep(3); }}
            style={{
              ...S.sec, marginTop: 16,
              // Mise en avant si aucun resultat
              ...(orders.length === 0 && searchSource ? {
                background: '#1A5276', color: 'white', border: 'none', fontWeight: 700,
              } : {}),
            }}>
            {orders.length === 0 && searchSource
              ? '→ Continuer sans identifier la commande'
              : 'Continuer sans identifier la commande'}
          </button>
          <button onClick={() => setStep(1)} style={S.sec}>← Retour</button>
        </div>
      )}

      {/* ═══ STEP 3: Produits ═══ */}
      {step === 3 && (
        <div>
          <div style={S.lbl}>Étape 3/5</div>
          <div style={S.ttl}>Quel(s) produit(s) ?</div>

          {productsLoading && <div style={{ textAlign: 'center', color: '#9CA3AF', fontSize: 12 }}>Chargement produits...</div>}

          {/* ━━━━━━ Choix : toute la commande OU certains produits ━━━━━━ */}
          {!productsLoading && products.length > 0 && (
            <div style={{ marginBottom: 12, padding: 8, background: '#F8FAFC', borderRadius: 10, border: '1px solid #E5E7EB' }}>
              <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 6, fontWeight: 600, textAlign: 'center' }}>
                Votre réclamation concerne :
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => setMode(false)} style={{
                  flex: 1, padding: '10px 8px', borderRadius: 8, border: '1.5px solid ' + (!wholeOrder ? '#1A5276' : '#E5E7EB'),
                  background: !wholeOrder ? '#1A5276' : 'white',
                  color: !wholeOrder ? 'white' : '#6B7280',
                  fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                }}>
                  🎯 Certains produits
                </button>
                <button onClick={() => setMode(true)} style={{
                  flex: 1, padding: '10px 8px', borderRadius: 8, border: '1.5px solid ' + (wholeOrder ? '#C0392B' : '#E5E7EB'),
                  background: wholeOrder ? '#C0392B' : 'white',
                  color: wholeOrder ? 'white' : '#6B7280',
                  fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                }}>
                  📦 Toute la commande
                </button>
              </div>
            </div>
          )}

          {/* ━━━━━━ Mode 'Toute la commande' : message seul, pas de liste ━━━━━━ */}
          {!productsLoading && wholeOrder && products.length > 0 && (
            <div style={{ padding: 16, borderRadius: 10, background: '#FDECEA', border: '1.5px solid #C0392B', marginBottom: 12, textAlign: 'center' }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#C0392B', marginBottom: 4 }}>
                📦 Tous les produits de la commande sont concernés
              </div>
              <div style={{ fontSize: 11, color: '#7F1D1D' }}>
                ({products.length} produit{products.length > 1 ? 's' : ''})
              </div>
            </div>
          )}

          {/* ━━━━━━ Mode 'Certains produits' : liste cochables ━━━━━━ */}
          {!productsLoading && !wholeOrder && products.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 600, marginBottom: 6 }}>
                Cochez le ou les produits concernés
              </div>
              {products.map((p, idx) => {
                const sel = selectedProducts.find(x => x.id === p.id);
                const isMatchedFirst = idx === 0 && (selectedProductSearch || productQuery) && sel;
                return (
                  <button key={p.id} onClick={() => toggleProduct(p)} style={{
                    ...S.pill(!!sel),
                    border: isMatchedFirst ? '1.5px solid #16A34A' : S.pill(!!sel).border,
                  }}>
                    <div style={{ width: 20, height: 20, borderRadius: 4, border: '1.5px solid ' + (sel ? '#1A5276' : '#D1D5DB'), background: sel ? '#1A5276' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                      {sel ? '✓' : ''}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.name}
                        {isMatchedFirst && <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: '#D5F5E3', color: '#166534' }}>VOTRE RECHERCHE</span>}
                      </div>
                      <div style={{ fontSize: 11, color: '#9CA3AF' }}>Commandé : {p.qty}</div>
                    </div>
                    {sel && (
                      <input type="number" min="1" value={sel.qtyReclamee}
                        onClick={e => e.stopPropagation()}
                        onChange={e => { e.stopPropagation(); updateQtyReclamee(p.id, e.target.value); }}
                        style={{ width: 48, textAlign: 'center', padding: '4px', borderRadius: 6, border: '1.5px solid #1A5276', fontSize: 14, fontWeight: 700, fontFamily: 'inherit' }}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* ━━━━━━ Ajout manuel (uniquement en mode 'Certains produits') ━━━━━━ */}
          {!wholeOrder && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 600, marginBottom: 6 }}>Ajouter un produit manuellement</div>
              <div style={{ display: 'flex', gap: 6 }}>
                <input style={{ ...S.input, flex: 1, fontSize: 13 }} type="text" value={manualProduct}
                  onChange={e => setManualProduct(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') addManualProduct(); }}
                  placeholder="Nom du produit..." />
                <button onClick={addManualProduct} disabled={!manualProduct.trim()} style={{ padding: '10px 14px', borderRadius: 10, border: 'none', background: manualProduct.trim() ? '#1A5276' : '#E5E7EB', color: manualProduct.trim() ? 'white' : '#9CA3AF', fontSize: 13, fontWeight: 600, cursor: manualProduct.trim() ? 'pointer' : 'default', fontFamily: 'inherit', flexShrink: 0 }}>+</button>
              </div>
            </div>
          )}

          {/* Produits manuels sélectionnés (uniquement en mode 'Certains produits') */}
          {!wholeOrder && selectedProducts.filter(p => p.source === 'manuel').map(p => (
            <div key={p.id} style={{ ...S.card, display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px' }}>
              <div style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>{p.name}</div>
              <input type="number" min="1" value={p.qtyReclamee}
                onChange={e => updateQtyReclamee(p.id, e.target.value)}
                style={{ width: 48, textAlign: 'center', padding: '4px', borderRadius: 6, border: '1.5px solid #D6E4F0', fontSize: 14, fontWeight: 700, fontFamily: 'inherit' }} />
              <button onClick={() => setSelectedProducts(prev => prev.filter(x => x.id !== p.id))} style={{ background: 'none', border: 'none', color: '#C0392B', fontSize: 16, cursor: 'pointer', padding: 4 }}>×</button>
            </div>
          ))}

          <button onClick={() => setStep(4)} disabled={selectedProducts.length === 0}
            style={selectedProducts.length > 0 ? S.pri : S.dis}>
            {selectedProducts.length > 0
              ? (wholeOrder ? 'Continuer (toute la commande)' : `Continuer (${selectedProducts.length} produit${selectedProducts.length > 1 ? 's' : ''})`)
              : 'Sélectionnez au moins un produit'}
          </button>
          <button onClick={() => setStep(selectedOrder ? 2 : 1)} style={S.sec}>← Retour</button>
        </div>
      )}

      {/* ═══ STEP 4: Type de problème ═══ */}
      {step === 4 && (
        <div>
          <div style={S.lbl}>Étape 4/5</div>
          <div style={S.ttl}>Quel problème ?</div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {getProblems(selectedProducts.length).map(p => (
              <button key={p.value} onClick={() => { setProblem(p.value); setSouhait(null); }} style={S.pill(problem === p.value)}>
                <div style={{ width: 28, height: 28, borderRadius: 6, background: problem === p.value ? '#1A5276' : '#F3F4F6', color: problem === p.value ? 'white' : '#6B7280', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>{p.icon}</div>
                <span>{p.label}</span>
              </button>
            ))}
          </div>

          <button onClick={() => setStep(5)} disabled={!problem}
            style={problem ? S.pri : S.dis}>Continuer</button>
          <button onClick={() => setStep(selectedProducts.length > 0 ? 3 : 1)} style={S.sec}>← Retour</button>
        </div>
      )}

      {/* ═══ STEP 5: Détails + photos ═══ */}
      {step === 5 && (
        <div>
          <div style={S.lbl}>Étape 5/5</div>
          <div style={S.ttl}>Détails</div>

          {/* Souhait de la pharmacie : options adaptées au problème */}
          {getSouhaitOptions(problem).length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#1A1D23', marginBottom: 8 }}>Que souhaitez-vous ?</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {getSouhaitOptions(problem).map(o => (
                  <button key={o.value} onClick={() => setSouhait(o.value)} style={S.pill(souhait === o.value)}>
                    <span>{o.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <textarea style={{ ...S.input, height: 100, resize: 'vertical' }} value={comment}
            onChange={e => setComment(e.target.value)}
            placeholder="Décrivez le problème (optionnel)..." />

          {/* Photos */}
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 600, marginBottom: 6 }}>
              Photos {problem === 'mauvaise_commande_recue' ? '(obligatoires)' : '(optionnel)'}
            </div>

            {/* Alerte specifique : commande d une autre pharmacie -> photo obligatoire */}
            {problem === 'mauvaise_commande_recue' && (
              <div style={{ padding: 12, borderRadius: 10, background: '#FDECEA', border: '1.5px solid #C0392B', marginBottom: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#C0392B', marginBottom: 4 }}>
                  📸 Photo de la caisse obligatoire
                </div>
                <div style={{ fontSize: 12, color: '#7F1D1D', lineHeight: 1.4 }}>
                  Joignez une photo de la caisse <strong>avec les produits visibles</strong> pour qu'on puisse identifier la commande que vous avez reçue par erreur.
                </div>
              </div>
            )}

            <button onClick={() => setCamOpen(true)} style={{ ...S.card, cursor: 'pointer', textAlign: 'center', border: '1.5px dashed #D1D5DB', color: '#6B7280', fontSize: 13, fontWeight: 600 }}>
              Prendre une photo
            </button>
            {photos.length > 0 && (
              <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                {photos.map((p, i) => (
                  <div key={i} style={{ position: 'relative' }}>
                    <img src={p.preview} alt="" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8 }} />
                    <button onClick={() => setPhotos(prev => prev.filter((_, j) => j !== i))} style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', border: 'none', background: '#C0392B', color: 'white', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <CameraCapture open={camOpen} onClose={() => setCamOpen(false)} onCapture={onCapture} />

          {/* Récap */}
          <div style={{ ...S.card, marginTop: 12, background: '#F8FAFC' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', marginBottom: 6 }}>Récapitulatif</div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{pharmacy?.name || phQuery}</div>
            {selectedOrder && <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{selectedOrder.refs.join(', ')} — {fmtDate(selectedOrder.date)}</div>}
            <div style={{ fontSize: 12, color: '#555', marginTop: 6 }}>
              {selectedProducts.map(p => p.name + ' (×' + p.qtyReclamee + ')').join(', ')}
            </div>
            <div style={{ fontSize: 12, color: '#1A5276', marginTop: 4, fontWeight: 600 }}>
              {getProblems(selectedProducts.length).find(p => p.value === problem)?.label}
            </div>
          </div>

          {error && <div style={{ color: '#C0392B', fontSize: 12, marginTop: 8, textAlign: 'center' }}>{error}</div>}

          {getSouhaitOptions(problem).length > 0 && !souhait && (
            <div style={{ fontSize: 12, color: '#D4680A', marginTop: 8, textAlign: 'center' }}>
              Indiquez ce que vous souhaitez ci-dessus pour envoyer
            </div>
          )}
          <button onClick={handleSubmit} disabled={submitting || (getSouhaitOptions(problem).length > 0 && !souhait)}
            style={(submitting || (getSouhaitOptions(problem).length > 0 && !souhait)) ? S.dis : S.pri}>
            {submitting ? 'Envoi en cours...' : 'Envoyer la réclamation'}
          </button>
          <button onClick={() => setStep(4)} style={S.sec}>← Retour</button>
        </div>
      )}
    </div>
  );
}
